/**
 * Catalogos del asistente RAT (destinatarios, transferencias, medidas de
 * seguridad, conservacion, bases juridicas, categorias de datos/titulares).
 *
 * En el Supabase original estos eran tablas administrables (`legal_bases`,
 * `data_categories`, `subject_categories`, `recipient_types`,
 * `security_measures`, `transfer_guarantee_types`, `retention_criteria`)
 * referenciadas por UUID, con `status`/`superseded_by_id` para deprecar sin
 * borrar -- pero ninguna llego a tener una UI de administracion real (ver
 * docs/Fase4b1_Wizard.md). El backend nuevo (D1) ya sigue el patron de
 * `activity_legal_bases`/`activity_data_categories`: codigo TEXT fijo en vez
 * de FK a una tabla de catalogo. Se replica aqui para las 5 colecciones
 * restantes -- listas cortas (5-15 items), Ley 21.719 + ISO 27001, que casi
 * nunca cambian. Si en el futuro se construye el modulo "catalogos
 * administrables", esto se reemplaza por un fetch real; hasta entonces son
 * datos, no una limitacion del modelo.
 */

export interface CatalogItem {
  code: string;
  label: string;
}

export interface LegalBasisCatalogItem extends CatalogItem {
  requiresReinforced: boolean;
}

export const LEGAL_BASES: LegalBasisCatalogItem[] = [
  { code: 'consentimiento', label: 'Consentimiento del titular', requiresReinforced: false },
  { code: 'contrato', label: 'Ejecución de un contrato', requiresReinforced: false },
  { code: 'obligacion_legal', label: 'Cumplimiento de una obligación legal', requiresReinforced: false },
  { code: 'interes_legitimo', label: 'Interés legítimo del responsable', requiresReinforced: false },
  { code: 'interes_vital', label: 'Interés vital del titular', requiresReinforced: false },
  { code: 'funcion_publica', label: 'Ejercicio de funciones de un órgano público', requiresReinforced: false },
  { code: 'consentimiento_explicito', label: 'Consentimiento explícito (datos sensibles)', requiresReinforced: true },
  { code: 'fines_estadisticos', label: 'Fines históricos, estadísticos o científicos', requiresReinforced: true },
];

export interface DataCategoryCatalogItem extends CatalogItem {
  isSensitive: boolean;
}

export const DATA_CATEGORIES: DataCategoryCatalogItem[] = [
  { code: 'identificativos', label: 'Identificativos', isSensitive: false },
  { code: 'contacto', label: 'Contacto', isSensitive: false },
  { code: 'academicos', label: 'Académicos', isSensitive: false },
  { code: 'laborales', label: 'Laborales', isSensitive: false },
  { code: 'economicos', label: 'Económicos / financieros', isSensitive: false },
  { code: 'bancarios', label: 'Datos bancarios', isSensitive: false },
  { code: 'uso_sistemas', label: 'Conducta / uso de sistemas', isSensitive: false },
  { code: 'salud', label: 'Salud', isSensitive: true },
  { code: 'biometricos', label: 'Biométricos', isSensitive: true },
  { code: 'origen_etnico', label: 'Origen étnico o racial', isSensitive: true },
  { code: 'afiliacion_sindical', label: 'Afiliación sindical', isSensitive: true },
  { code: 'afiliacion_politica', label: 'Afiliación política', isSensitive: true },
  { code: 'creencias', label: 'Creencias o convicciones', isSensitive: true },
  { code: 'vida_sexual', label: 'Vida sexual u orientación sexual', isSensitive: true },
  { code: 'socioeconomica', label: 'Situación socioeconómica', isSensitive: true },
];

export interface SubjectCategoryCatalogItem extends CatalogItem {
  isProtectedGroup: boolean;
}

export const SUBJECT_CATEGORIES: SubjectCategoryCatalogItem[] = [
  { code: 'estudiantes', label: 'Estudiantes', isProtectedGroup: false },
  { code: 'postulantes', label: 'Postulantes', isProtectedGroup: false },
  { code: 'alumni', label: 'Egresados / alumni', isProtectedGroup: false },
  { code: 'academicos', label: 'Académicos/as', isProtectedGroup: false },
  { code: 'funcionarios', label: 'Funcionarios/as', isProtectedGroup: false },
  { code: 'honorarios', label: 'Personal a honorarios', isProtectedGroup: false },
  { code: 'investigadores', label: 'Investigadores/as', isProtectedGroup: false },
  { code: 'proveedores', label: 'Proveedores', isProtectedGroup: false },
  { code: 'pacientes', label: 'Pacientes', isProtectedGroup: false },
  { code: 'participantes_inv', label: 'Participantes de investigación', isProtectedGroup: false },
  { code: 'participantes_ext', label: 'Participantes de extensión', isProtectedGroup: false },
  { code: 'apoderados', label: 'Apoderados', isProtectedGroup: false },
  { code: 'nna', label: 'Niños, niñas y adolescentes (NNA)', isProtectedGroup: true },
];

export interface RecipientTypeCatalogItem extends CatalogItem {
  isProcessor: boolean;
}

export const RECIPIENT_TYPES: RecipientTypeCatalogItem[] = [
  { code: 'interno', label: 'Uso interno', isProcessor: false },
  { code: 'encargado', label: 'Encargado de tratamiento', isProcessor: true },
  { code: 'organismo_publico', label: 'Organismo público', isProcessor: false },
  { code: 'institucion_educativa', label: 'Institución educativa', isProcessor: false },
  { code: 'red_salud', label: 'Red de salud', isProcessor: false },
  { code: 'entidad_bancaria', label: 'Entidad bancaria', isProcessor: false },
];

export const SECURITY_MEASURES: CatalogItem[] = [
  { code: 'acceso_rol', label: 'Control de acceso por rol' },
  { code: 'mfa', label: 'Autenticación de múltiples factores' },
  { code: 'cifrado_transito', label: 'Cifrado en tránsito' },
  { code: 'cifrado_reposo', label: 'Cifrado en reposo' },
  { code: 'respaldos', label: 'Respaldos periódicos' },
  { code: 'trazabilidad', label: 'Trazabilidad / bitácora de accesos' },
  { code: 'confidencialidad', label: 'Acuerdos de confidencialidad' },
  { code: 'capacitacion', label: 'Capacitación al personal' },
  { code: 'anonimizacion', label: 'Anonimización / seudonimización' },
  { code: 'acceso_fisico', label: 'Control de acceso físico' },
];

export const TRANSFER_GUARANTEE_TYPES: CatalogItem[] = [
  { code: 'clausulas_tipo', label: 'Cláusulas contractuales tipo' },
  { code: 'nivel_adecuado', label: 'País con nivel adecuado de protección' },
  { code: 'consentimiento', label: 'Consentimiento del titular' },
  { code: 'normas_vinculantes', label: 'Normas corporativas vinculantes' },
  { code: 'sin_garantia', label: 'Sin garantía formal (declarar riesgo)' },
];

export const RETENTION_CRITERIA: CatalogItem[] = [
  { code: 'plazo_legal', label: 'Plazo legal' },
  { code: 'vinculo_mas_n', label: 'Duración del vínculo + N años' },
  { code: 'fin_finalidad', label: 'Hasta el fin de la finalidad' },
  { code: 'politica_institucional', label: 'Política institucional' },
  { code: 'sin_criterio', label: 'Sin criterio definido aún' },
];
